import { Box, Chip, Paper, Radio, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { formatQty, shortfallAgainstFill } from '../utils/bulkLotNdcStatus';
import { warningTextTone } from '../utils/warningTone';

/** A lot inside this many days of expiry is flagged, without being withheld. */
const EXPIRING_SOON_DAYS = 30;

/**
 * POM-6010 — the stocked lots for the selected NDC, earliest expiry first.
 *
 * Extracted from ChangeNdcLotDialog by POM-6121, which added a second, mutually exclusive way to
 * choose a lot beneath it. The parent decides whether this list is on offer at all: a fill is
 * either allocated from stock or hand-recorded, never both, so in manual mode it is not rendered.
 */
const ChangeNdcLotStockLots = ({
  lots,
  selectedNdc,
  selectedLotId,
  recordedLotIds,
  seededLotCaption,
  fillQuantity,
  shortfallAllowed,
  facilityName,
  onSelectLot,
}) => (
  <>
    <Stack direction='row' justifyContent='space-between' alignItems='baseline' sx={{ mb: 1 }}>
      <Typography variant='subtitle2'>
        {selectedNdc ? `Lots for ${selectedNdc}` : 'Lots'}
      </Typography>
      {/* Says what was actually seeded, from the data rather than from the live selection: the
          sentence is about how the dialog opened, so it must not change wording as the user
          clicks around. */}
      <Typography variant='caption' color='text.secondary'>
        {`Earliest expiry first · ${seededLotCaption}`}
      </Typography>
    </Stack>

    {!selectedNdc && (
      <Typography variant='body2' color='text.secondary'>
        Select an NDC to see its lots.
      </Typography>
    )}

    {selectedNdc && lots.length === 0 && (
      <Typography variant='body2' color='text.secondary'>
        {/* POM-6121 - what empty MEANS depends on the switch. With the claim available the list
            holds every in-date lot with any stock, so empty is no stock at all; without it a lot
            that cannot cover the fill is still withheld, and saying "no stock" would send the
            operator hunting for units sitting on the shelf. */}
        {shortfallAllowed
          ? `No lot in stock at ${facilityName ?? 'this facility'}.`
          : `No lot in stock can cover the full quantity of ${formatQty(fillQuantity)} at ${
              facilityName ?? 'this facility'
            }.`}
      </Typography>
    )}

    <Stack spacing={1}>
      {lots.map((lot) => {
        const isSelected = lot.inventoryLotId === selectedLotId;
        const expiringSoon =
          Number(lot.daysUntilExpiry) <= EXPIRING_SOON_DAYS && Number(lot.daysUntilExpiry) >= 0;

        return (
          /* A real label around a real radio, all sharing one name, so the list is a native radio
             group: Tab reaches it, the arrow keys move through it, and Space selects. It was
             mouse-only until POM-6121 put a keyboard-operable panel underneath it, and one of the
             two being reachable is worse than neither. */
          <Paper
            key={lot.inventoryLotId}
            component='label'
            variant='outlined'
            sx={{
              p: 1.5,
              display: 'block',
              cursor: 'pointer',
              borderColor: isSelected ? 'primary.main' : undefined,
              borderWidth: isSelected ? 2 : 1,
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Radio
                checked={isSelected}
                onChange={() => onSelectLot(lot.inventoryLotId)}
                name='stocked-lot'
                size='small'
                inputProps={{ 'aria-label': lot.lotNumber }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction='row' spacing={0.75} alignItems='center'>
                  <Typography variant='body2'>{lot.lotNumber}</Typography>
                  {/* Marked on every recorded lot, not only the seeded one, so a multi-lot fill
                      still shows which lots it is currently on. Uppercase to read as a marker
                      beside the DEF and PREF chips rather than as a label. */}
                  {recordedLotIds.has(lot.inventoryLotId) && (
                    <Chip label='CURRENT' size='small' variant='outlined' />
                  )}
                </Stack>
                <Typography variant='caption' color='text.secondary'>
                  {`Exp ${String(lot.expirationDate).slice(0, 10)}`}
                  {expiringSoon ? ` · Expiring · ${lot.daysUntilExpiry}d` : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant='caption' color='text.secondary' component='div'>
                  Available
                </Typography>
                <Typography variant='body2'>{formatQty(lot.remainingQty)}</Typography>
                {/* POM-6121 — said on the row, not only after it is picked: with two short lots
                    on offer the operator compares them before choosing, and the shortfall is the
                    thing they are comparing. */}
                {shortfallAgainstFill({ lot, fillQuantity }) > 0 && (
                  <Typography variant='caption' component='div' sx={{ color: warningTextTone }}>
                    {`${formatQty(shortfallAgainstFill({ lot, fillQuantity }))} ${SYSTEM_DEFICIT.toLowerCase()}`}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  </>
);

ChangeNdcLotStockLots.propTypes = {
  /** The selected NDC's usable lots, already ordered earliest expiry first. */
  lots: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedNdc: PropTypes.string,
  selectedLotId: PropTypes.string,
  /** The lots the row records today, for the CURRENT marker. */
  recordedLotIds: PropTypes.instanceOf(Set).isRequired,
  seededLotCaption: PropTypes.string.isRequired,
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** True when a lot that cannot cover the fill is offered with its shortfall claimed. */
  shortfallAllowed: PropTypes.bool,
  facilityName: PropTypes.string,
  onSelectLot: PropTypes.func.isRequired,
};

ChangeNdcLotStockLots.defaultProps = {
  selectedNdc: null,
  selectedLotId: null,
  fillQuantity: '',
  shortfallAllowed: false,
  facilityName: null,
};

export default ChangeNdcLotStockLots;
